import {
  AttributionDetails,
  BucketPriority,
  Image,
  ImageMeta,
  ImageType,
  SessionMode,
  SpaceData,
} from 'glance-datatypes';
import Constants, { Partners } from './Constants';
import {
  Duration,
  DurationUnit,
  GlanceData,
  GlanceTime,
  Region,
  RelativeTime,
  Role,
} from 'glance-datatypes';
import {
  TimeZone,
  chinaTimezone,
  idTimezone,
  indiaTimezone,
  krTimezone,
  usTimezone,
  utcTimeZone,
} from './components/Clock';
import { BeaconType } from './pages/drafts/types';

const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'July',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const millnames = ['', ' K', ' M', ' B', ' T'];

export const typeMap = {
  WALLPAPER: 1,
  STORY: 2,
  RESERVED_STORY: 3,
  SPONSORED: 4,
};

export const durationUnitMap = {
  HOURS: 0,
  DAYS: 1,
};

export const glanceMetaStateMap = {
  INMOBI_APPROVED: 0,
  STUDIO_APPROVED: 1,
  PARTNER_APPROVED: 2,
  PARTNER_REJECTED: 3,
  PARTNER_REMOVED: 4,
  INMOBI_REMOVED: 5,
  STUDIO_REMOVED: 6,
  INTERNAL_PENDING: 7,
  INTERNAL_APPROVED: 8,
  INTERNAL_REJECTED: 9,
};

export const modeViewMap = {
  ANY: 0,
  DEFAULT: 1,
  BINGE: 2,
  HIGHLIGHTS: 3,
  GAME_REWARDED: 4,
};

export const regionMap = {
  US: 1,
  IN: 2,
  CN: 3,
  KR: 4,
  ID: 5,
  HK: 6,
  KH: 7,
  MM: 8,
  MO: 9,
  MY: 10,
  PH: 11,
  PK: 12,
  RU: 13,
  SG: 14,
  TH: 15,
  TW: 16,
  VN: 17,
  LK: 18,
  LA: 19,
  KE: 20,
  MA: 21,
  BD: 22,
  NP: 23,
  AU: 24,
  NZ: 25,
  MX: 26, // Mexico
  BR: 27, // Brazil
  CO: 28, // Colombia
  JP: 29, // Japan
  AT: 30, // Austria
  DE: 31, // Germany
  NG: 32, // Nigeria
  GB: 33, // Great Britain
  ISLAMIC: 97,
  NON_ISLAMIC: 98,
  GLOBAL: 99,
  NO_REGION: 100,
  ALL_REGION: 101,
};

export const typeFilters = {
  0: ['WALLPAPER', 'STORY', 'SPONSORED'],
  1: ['WALLPAPER'],
  2: ['STORY'],
  3: ['SPONSORED'],
  4: ['NEWS'],
  5: ['FEATURE'],
  6: ['GLANCETV'],
};

export const expiryFilters = ['ALL', 'ACTIVE', 'EXPIRED'];
export const stateFilters = {
  0: ['STUDIO_APPROVED', 'INTERNAL_APPROVED'],
  1: ['PARTNER_APPROVED', 'STUDIO_PAUSED'],
  2: ['PARTNER_REJECTED'],
  3: ['PARTNER_REMOVED', 'STUDIO_REMOVED'],
};

export const stateFiltersInternal = {
  0: ['INTERNAL_PENDING'],
  1: ['INTERNAL_APPROVED', 'PARTNER_APPROVED'],
  2: ['INTERNAL_REJECTED'],
};

export const regionFilters = {
  1: 'US',
  2: 'IN',
  3: 'CN',
  4: 'KR',
  5: 'ID',
  6: 'HK',
  7: 'KH',
  8: 'MM',
  9: 'MO',
  10: 'MY',
  11: 'PH',
  12: 'PK',
  13: 'RU',
  14: 'SG',
  15: 'TH',
  16: 'TW',
  17: 'VN',
  18: 'LK',
  19: 'LA',
  20: 'KE',
  21: 'MA',
  22: 'BD',
  23: 'NP',
  24: 'AU',
  25: 'NZ',
  26: 'MX', // Mexico
  27: 'BR', // Brazil
  28: 'CO', // Colombia
  29: 'JP', // Japan
  97: 'ISLAMIC',
  98: 'NON_ISLAMIC',
  99: 'GLOBAL',
  100: 'NO_REGION',
  101: 'ALL_REGION',
};

export const regionNames = {
  [Region.AU]: 'Australia',
  [Region.CN]: 'China',
  [Region.IN]: 'India',
  [Region.US]: 'USA',
  [Region.KR]: 'Korea',
  [Region.ID]: 'Indonesia',
  [Region.HK]: 'Hong Kong',
  [Region.KH]: 'Cambodia',
  [Region.MM]: 'Myanmar',
  [Region.MO]: 'Macau',
  [Region.MY]: 'Malaysia',
  [Region.PH]: 'Philippines',
  [Region.PK]: 'Pakistan',
  [Region.RU]: 'Russia',
  [Region.SG]: 'Singapore',
  [Region.TH]: 'Thailand',
  [Region.TW]: 'Taiwan',
  [Region.VN]: 'Vietnam',
  [Region.LK]: 'Sri Lanka',
  [Region.LA]: 'Laos',
  [Region.KE]: 'Kenya',
  [Region.MA]: 'Morocco',
  [Region.BD]: 'Bangladesh',
  [Region.NP]: 'Nepal',
  [Region.NZ]: 'New Zealand',
  [Region.NG]: 'Nigeria',
  [Region.GB]: 'Great Britain',
  [Region.DE]: 'Germany',
  [Region.AT]: 'Austria',
  [Region.GLOBAL]: 'Global',
  [Region.ISLAMIC]: 'Islamic',
  [Region.NON_ISLAMIC]: 'Non Islamic',
  [Region.MX]: 'Mexico',
  [Region.BR]: 'Brazil',
  [Region.CO]: 'Colombia',
  [Region.JP]: 'Japan',
};

export const roleNames = {
  [Role.ADMIN]: 'admin',
  [Role.CURATOR]: 'curator',
  [Role.DESIGNER]: 'designer',
  [Role.MODERATOR]: 'moderator',
  [Role.PARTNER]: 'partner',
};

export const allRegions = [
  Region.CN,
  Region.KR,
  Region.AU,
  Region.NZ,
  Region.PH,
  Region.SG,
  Region.IN,
  Region.KH,
  Region.MM,
  Region.US,
  Region.TH,
  Region.VN,
  Region.ID,
  Region.MY,
  Region.GLOBAL,
  Region.HK,
  Region.LK,
  Region.LA,
  Region.MA,
  Region.KE,
  Region.BD,
  Region.NP,
  Region.TW,
  Region.RU,
  Region.PK,
  Region.ISLAMIC,
  Region.NON_ISLAMIC,
  Region.MX,
  Region.BR,
  Region.CO,
  Region.JP,
  Region.AT,
  Region.DE,
  Region.NG,
  Region.GB,
];

export const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const PARTNERS_WITH_RELAXED_EXPIRY = [
  Partners.TEST_PARTNER_ID,
  Partners.OPPO_PARTNER_ID,
  Partners.OPPO_API_PARTNER_ID,
  Partners.VIVO_PARTNER_ID,
];

export const DEV_ENVS = ['dev', 'localhost'];

class Utils {
  public static getReadableAbsoluteTime(epoch: number) {
    if (!epoch || epoch === 0) {
      return '';
    }
    let current: Date = new Date(0);
    current.setUTCSeconds(epoch);
    let date: Date = new Date(current);

    const dateString =
      date.getDate() >= 10 ? date.getDate() : '0' + date.getDate();
    const minuteString =
      date.getMinutes() >= 10
        ? '' + date.getMinutes()
        : '0' + date.getMinutes();
    const hourString =
      date.getHours() >= 10 ? '' + date.getHours() : '0' + date.getHours();

    return (
      hourString +
      ':' +
      minuteString +
      ', ' +
      days[date.getDay()] +
      ' ' +
      months[date.getMonth()] +
      ' ' +
      dateString +
      ' ' +
      date.getFullYear()
    );
  }

  public static getReadableRelativeTime(relativeTime: RelativeTime) {
    if (!relativeTime) {
      return '';
    }
    const dateString =
      relativeTime.date >= 10 ? relativeTime.date : '0' + relativeTime.date;
    const minuteString =
      relativeTime.minute >= 10
        ? '' + relativeTime.minute
        : '0' + relativeTime.minute;
    const hourString =
      relativeTime.hour >= 10
        ? '' + relativeTime.hour
        : '0' + relativeTime.hour;

    return (
      hourString +
      ': ' +
      minuteString +
      ', ' +
      months[relativeTime.month - 1] +
      ' ' +
      dateString +
      ' ' +
      relativeTime.year
    );
  }

  // returns data in yyyy-mm-dd format
  public static getDateInYyyyMmDd(date: Date) {
    return date.toISOString().split('T')[0];
  }

  public static getReadableNumber(number: number): string {
    if (!number) {
      return '';
    }
    const base = 1000;
    const e = Math.floor(Math.log(number) / Math.log(base));
    return Math.floor(number / Math.pow(base, e)) + ' ' + millnames[e];
  }

  public static getImageUrl(
    cdnPrefix: string,
    image: Image,
    type: ImageType,
  ): string {
    if (image) {
      let imageUrl = cdnPrefix + 'wallpaper/' + image.id + '.webp';
      image.imageMetas.forEach((meta) => {
        if (meta.type === type) {
          imageUrl = Utils.getImagePath(cdnPrefix, image.id, meta);
        }
      });
      return imageUrl;
    }

    return null;
  }
  public static getImageUrlForSpaces(
    cdnPrefix: string,
    image: SpaceData,
  ): string {
    if (image && image.imageMap) {
      const imagePath = image.imageMap.baseImage
        ? image.imageMap.baseImage
        : image.imageMap.l0Large
        ? image.imageMap.l0Large
        : image.imageMap.l0Medium
        ? image.imageMap.l0Medium
        : image.imageMap.lPlus;
      return cdnPrefix + imagePath;
    }
    return null;
  }

  public static getImagePath(
    cdnPrefix: string,
    imageId: string,
    meta?: ImageMeta,
  ): string {
    switch (meta.type) {
      case ImageType.BLUR:
        return cdnPrefix + 'blur/' + imageId + '.webp';
      case ImageType.LEGACY:
        return cdnPrefix + 'wallpaper/' + imageId + '.webp';
      case ImageType.STUDIO_ORIGINAL:
        return (
            cdnPrefix +
            'original/' +
            meta.width +
            'x' +
            meta.height +
            '/' +
            imageId +
            '.jpg');
      case ImageType.ORIGINAL:
        return (
          cdnPrefix +
          'dynamic/' +
          meta.width +
          'x' +
          meta.height +
          '/' +
          imageId +
          '.webp'
        );
      default:
        return cdnPrefix + 'wallpaper/' + imageId + '.webp';
    }
  }

  public static getBubbleImagePath(imagePath: string): string {
    return Constants.CDN_PREFIX + imagePath;
  }

  public static readableAttribution(attribution: AttributionDetails): string {
    if (!attribution) {
      return null;
    }

    if (attribution.source) {
      return attribution.source;
    }

    return null;
  }

  public static openNewTab(url: string) {
    window.open(url, '_blank');
  }

  public static getParameterByName = (
    name: string,
    url = window.location.href,
  ): string => {
    const nameStr = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + nameStr + '(=([^&#]*)|&|#|$)'),
      results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  public static truncate(string: string, length: number): string {
    if (string.length < length) {
      return string;
    } else {
      const suffix = '...';
      return string.substring(0, length - suffix.length) + suffix;
    }
  }

  public static getReadableRegionName(region: Region) {
    if (regionNames[region]) {
      return regionNames[region];
    } else {
      console.error(
        'What the hell! You forgot to add region specific values here',
      );
      return '';
    }
  }

  public static getReadableRoleName(role: Role) {
    if (roleNames[role]) {
      return roleNames[role];
    } else {
      console.error('Incorrect or missing role name');
      return '';
    }
  }

  public static getEpochFinalInSecs(glanceTime: GlanceTime): number {
    let startDate: Date;
    if (glanceTime.startTime.absoluteTime) {
      const startEpoch: number =
        glanceTime.startTime.absoluteTime.timeInSecs * 1000;
      return startEpoch / 1000;
    } else {
      const relativeTime: RelativeTime = glanceTime.startTime.relativeTime;
      startDate = new Date(
        relativeTime.year,
        relativeTime.month - 1,
        relativeTime.date,
        relativeTime.hour,
        relativeTime.minute,
        0,
        0,
      );
      return startDate.getTime() / 1000;
    }
  }

  public static getStartDate(region: Region, glanceTime: GlanceTime): Date {
    let timezone: TimeZone = this.getTimezone(region);
    let startDate: Date;
    if (glanceTime.startTime.absoluteTime) {
      const startEpoch: number =
        glanceTime.startTime.absoluteTime.timeInSecs * 1000;
      startDate = new Date(startEpoch);
      startDate.setHours(startDate.getUTCHours() + timezone.hours);
      startDate.setMinutes(startDate.getUTCMinutes() + timezone.minutes);
    } else {
      const relativeTime: RelativeTime = glanceTime.startTime.relativeTime;
      startDate = new Date(
        relativeTime.year,
        relativeTime.month - 1,
        relativeTime.date,
        relativeTime.hour,
        relativeTime.minute,
        0,
        0,
      );
    }
    return startDate;
  }

  static getTimezone(region: Region) {
    switch (region) {
      case Region.CN:
        return chinaTimezone;

      case Region.IN:
        return indiaTimezone;

      case Region.US:
        return usTimezone;

      case Region.KR:
        return krTimezone;

      case Region.ID:
        return idTimezone;

      case Region.GLOBAL:
        return utcTimeZone;

      case Region.ISLAMIC:
        return utcTimeZone;

      case Region.NON_ISLAMIC:
        return utcTimeZone;

      default:
        return utcTimeZone;
    }
  }

  static isExpired(glanceTime: GlanceTime, timezone: TimeZone): boolean {
    if (glanceTime.startTime.absoluteTime) {
      return (
        glanceTime.startTime.absoluteTime.timeInSecs * 1000 +
          Utils.getMillis(glanceTime.duration) <
        new Date().getTime()
      );
    } else {
      const relativeTime: RelativeTime = glanceTime.startTime.relativeTime;
      const startDate = new Date(
        relativeTime.year,
        relativeTime.month - 1,
        relativeTime.date,
        relativeTime.hour,
        relativeTime.minute,
        0,
        0,
      );

      const newDate = new Date(startDate);
      newDate.setUTCFullYear(relativeTime.year);
      newDate.setUTCMonth(relativeTime.month - 1);
      newDate.setUTCDate(relativeTime.date);
      newDate.setUTCHours(relativeTime.hour);
      newDate.setUTCMinutes(relativeTime.minute);
      newDate.setUTCSeconds(0);
      newDate.setUTCMilliseconds(0);
      const startEpochFinalInSecs = startDate.getTime() / 1000;
      const currentdate = new Date();

      return (
        startEpochFinalInSecs + Utils.getMillis(glanceTime.duration) / 1000 <
        Math.floor(currentdate.getTime() / 1000)
      );
    }
  }

  static getOffsetDifference(currentOffset: number, timezone: TimeZone) {
    console.log(currentOffset, timezone);
    currentOffset = -1 * currentOffset;
    if (currentOffset < 0) {
      if (timezone.hours < 0) {
        return timezone.hours * 60 + timezone.minutes - currentOffset;
      } else {
        return (
          Math.abs(currentOffset) +
          Math.abs(timezone.hours * 60 + timezone.minutes)
        );
      }
    } else {
      if (timezone.hours < 0) {
        return (
          Math.abs(currentOffset) +
          Math.abs(timezone.hours * 60 + timezone.minutes)
        );
      } else {
        return timezone.hours * 60 + timezone.minutes - currentOffset;
      }
    }
  }

  static getMillis(duration: Duration): number {
    if (duration) {
      if (duration.unit === DurationUnit.DAYS) {
        return duration.length * 24 * 60 * 60 * 1000;
      } else if (duration.unit === DurationUnit.HOURS) {
        return duration.length * 60 * 60 * 1000;
      } else if (duration.unit === DurationUnit.MINUTES) {
        return duration.length * 60 * 1000;
      } else if (duration.unit === DurationUnit.SECONDS) {
        return duration.length * 1000;
      }
    }
    return 0;
  }

  static getUserLevelExpiry(duration: Duration): string {
    if (duration !== null && duration !== undefined) {
      if (duration.unit === DurationUnit.DAYS) {
        return `${duration.length} ${duration.length > 1 ? 'days' : 'day'}`;
      } else if (duration.unit === DurationUnit.HOURS) {
        return `${duration.length} ${duration.length > 1 ? 'hours' : 'hour'}`;
      }
    }
  }

  static getPeekLink(data: GlanceData): string {
    if (data.peek) {
      if (data.peek.video) {
        const meta = data.peek.video.video.meta;
        if (meta.facebook) {
          return 'https://facebook.com/' + meta.facebook.id;
        } else if (meta.youtube) {
          return 'https://youtu.be/' + meta.youtube.id;
        }
        if (meta.instagram) {
          return 'https://www.instagram.com/p/' + meta.instagram.id;
        }
        if (meta.youku) {
          return 'http://v.youku.com/v_show/id_' + meta.youku.id + '.html';
        }
      } else if (data.peek.article) {
        return data.peek.article.cta.url;
      }
    }
    return null;
  }

  static getSummary(data: GlanceData): string {
    if (data.peek) {
      if (data.peek.article) {
        return data.peek.article.summary;
      } else if (data.peek.video) {
        return data.peek.video.summary;
      } else if (data.peek.webpeek) {
        return data.peek.webpeek.summary;
      } else if (data.peek.nativeVideoPeek) {
        return data.peek.nativeVideoPeek.summary;
      }
    }
    return null;
  }

  static getDomainPrefix(): string {
    const href = window.location.href;
    let start = href.indexOf('://');
    start = start === -1 ? 0 : start + 3;
    let end = href.indexOf('.');
    end = end === -1 ? href.indexOf(': ', start) : end;
    end = end === -1 ? href.indexOf('/') : end;
    const domainPrefix = href.substr(start, end - start);
    if (domainPrefix === 'studio') {
      return 'Curators';
    } else {
      return 'Partners';
    }
  }

  static debounce(func, delay) {
    let debounceTimer;
    return function() {
      const context = this;
      const args = arguments;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => func.apply(context, args), delay);
    };
  }

  static parseGlancePairs(pairs) {
    if (Array.isArray(pairs)) {
      pairs.forEach((pair) => {
        if (pair.data && pair.data.type) {
          pair.data.type = typeMap[pair.data.type];
        }
        if (pair.data && pair.data.renderSessionProperties) {
          pair.data.renderSessionProperties = (
            pair.data.renderSessionProperties || []
          ).map((type) => {
            return modeViewMap[type];
          });
        }
        if (pair.metas) {
          const partners = [];
          const partnerExperiments = [];
          if (Array.isArray(pair.metas)) {
            pair.meta = pair.metas[0];
            pair.metas.forEach((m) => {
              const { partnerId, experiments } = m;
              partners.push(partnerId);
              const partnerExpObj = {
                partnerId,
                experiments,
              };
              partnerExperiments.push(partnerExpObj);
            });
            pair.meta.partnerIds = partners;
            pair.meta.partnerExperiments = partnerExperiments;
          }
        }
        pair.meta.region = regionMap[pair.meta.region];
        pair.meta.state = glanceMetaStateMap[pair.meta.state];
        if (pair.meta.glanceTime) {
          if (pair.meta.glanceTime.duration) {
            pair.meta.glanceTime.duration.unit =
              durationUnitMap[pair.meta.glanceTime.duration.unit];
          }
          if (pair.meta.glanceTime.userLevelDuration) {
            pair.meta.glanceTime.userLevelDuration.unit =
              durationUnitMap[pair.meta.glanceTime.userLevelDuration.unit];
          }
        }
      });
    }
  }

  static isExpiryRelaxed(partnerIds: string[]) {
    return partnerIds.every(
      (partnerId) => PARTNERS_WITH_RELAXED_EXPIRY.indexOf(partnerId) >= 0,
    );
  }
  //should be removed when we deprecate renderSessionMode
  static getRenderSessionData = (data: any) => {
    if (data) {
      let renderSessionMode = data.renderSessionMode;
      let renderSessionProperties = data.renderSessionProperties || [];
      renderSessionProperties = renderSessionProperties.filter(
        (item) => item != SessionMode.HIGHLIGHTS,
      );
      if (renderSessionProperties && renderSessionProperties.length) {
        return renderSessionProperties;
      } else if (renderSessionMode == SessionMode.HIGHLIGHTS) {
        return [SessionMode.BINGE];
      } else if (
        renderSessionMode == SessionMode.BINGE ||
        renderSessionMode == SessionMode.DEFAULT
      ) {
        return [renderSessionMode];
      } else if (renderSessionMode == SessionMode.ANY) {
        return [SessionMode.DEFAULT, SessionMode.BINGE];
      } else {
        return [];
      }
    }
    return [];
  }

  static validURL(str) {
    let pattern = new RegExp(
      '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*',
      'i',
    ); // port and path
    return !!pattern.test(str);
  }

  static isValidBeaconURL(beaconUrl, beaconType, beaconPartner) {
    let isBeaconURLError = false;
    let hostName: string;
    if (beaconUrl) {
      hostName = new URL(beaconUrl).hostname;
    }
    switch (beaconPartner) {
      case 'Appsflyer':
        if (
          beaconType === BeaconType.RENDER &&
          !(hostName === 'impression.appsflyer.com')
        ) {
          isBeaconURLError = true;
        } else if (
          beaconType === BeaconType.CTA &&
          !(hostName === 'app.appsflyer.com')
        ) {
          isBeaconURLError = true;
        }
        break;
      case 'Branch':
        if (
          beaconType === BeaconType.RENDER &&
          !(hostName === 'impression.link' || hostName === 'impression')
        ) {
          isBeaconURLError = true;
        }
        break;
      case 'Kochava':
        if (
          beaconType === BeaconType.RENDER &&
          !(hostName === 'imp.control.kochava.com')
        ) {
          isBeaconURLError = true;
        } else if (
          beaconType === BeaconType.CTA &&
          !(hostName === 'control.kochava.com')
        ) {
          isBeaconURLError = true;
        }
        break;
      case 'DCM':
        if (
          beaconType === BeaconType.RENDER &&
          !(
            beaconUrl.includes('trackimp') &&
            beaconUrl.includes('ord=$IMP_ID') &&
            beaconUrl.includes('dc_rdid=$GPID')
          )
        ) {
          isBeaconURLError = true;
        } else if (
          beaconType === BeaconType.CTA &&
          !(
            beaconUrl.includes('trackclk') &&
            beaconUrl.includes('ord=$IMP_ID') &&
            beaconUrl.includes('dc_rdid=$GPID')
          )
        ) {
          isBeaconURLError = true;
        }
        break;
      case 'Sizemek':
        if (
          beaconType === BeaconType.RENDER &&
          !(
            beaconUrl.includes('cn=display') &&
            beaconUrl.includes('ord=$IMP_ID')
          )
        ) {
          isBeaconURLError = true;
        } else if (
          beaconType === BeaconType.CTA &&
          !(beaconUrl.includes('cn=trd') && beaconUrl.includes('ord=$IMP_ID'))
        ) {
          isBeaconURLError = true;
        }
        break;
      case 'DV':
        if (
          beaconType === BeaconType.RENDER &&
          !(hostName === 'tps.doubleverify.com')
        ) {
          isBeaconURLError = true;
        }
        break;
      case 'Neilson':
        if (
          beaconType === BeaconType.RENDER &&
          !(
            hostName.includes('imrworldwide') &&
            beaconUrl.includes(
              'c9=devid,$GPID&c13=asid,P471A0E0B-BD0B-4A3B-98AC-AE7C8F9765BD&uoo=$LIMIT_AD_TRACKING&r=$IMP_CB',
            )
          )
        ) {
          isBeaconURLError = true;
        } else if (
          beaconType === BeaconType.CTA &&
          !beaconUrl.includes(
            'c9=devid,$GPID&c13=asid,P471A0E0B-BD0B-4A3B-98AC-AE7C8F9765BD&uoo=$LIMIT_AD_TRACKING&r=$IMP_CB',
          )
        ) {
          isBeaconURLError = true;
        }
        break;
      default:
        break;
    }
    return isBeaconURLError;
  }

  static getGambitUrl = () => {
    let currentUrl = '';
    switch (window.location.hostname) {
      case 'studio-staging.glance.inmobi.com':
        currentUrl = Constants.GAMBIT_URL_STAGING;
        break;
      case 'studio.glance.inmobi.com':
        currentUrl = Constants.GAMBIT_URL_PROD;
        break;
      default:
        currentUrl = Constants.GAMBIT_URL_DEV;
        break;
    }
    return currentUrl;
  }

  static getBucketPriority = (priority: string) => {
    let priorityValue = priority.toLowerCase();
    switch (priorityValue) {
      case 'house':
        return BucketPriority.HOUSE;
      case 'sponsorship':
        return BucketPriority.SPONSORSHIP;
      case 'high_priority':
        return BucketPriority.HIGH_PRIORITY;
      case 'low_priority':
        return BucketPriority.LOW_PRIORITY;
      case 'price_priority':
        return BucketPriority.PRICE_PRIORITY;
      default:
        return null;
    }
  }

  public static getFormattedTime = (unixTime: number): string => {
    const date = new Date(unixTime * 1000);
    const day = date.getDate();
    const month = MONTHS[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours();
    const minute = date.getMinutes();

    return `${month} ${day}, ${year} ${hours}:${minute} IST`;
  }

  static getContextualEmojiUrl = () => {
    let currentUrl = '';
    switch (window.location.hostname) {
      case 'studio-staging.glance.inmobi.com':
        currentUrl = Constants.CONTEXTUAL_EMOJI_STAGING;
        break;
      case 'studio.glance.inmobi.com':
        currentUrl = Constants.CONTEXTUAL_EMOJI_PROD;
        break;
      default:
        currentUrl = Constants.CONTEXTUAL_EMOJI_DEV;
        break;
    }
    return currentUrl;
  }

  public static getEnvDetails() {
    const hostName = window.location.hostname;
    if (DEV_ENVS.some((el) => hostName.includes(el))) {
      return 'dev';
    }
    if (hostName.includes('staging')) {
      return 'staging';
    }
    return 'prod';
  }

  static getCookieValueByName = (cookieName: string) => {
    const cookies = document.cookie.split(';');
    for (let count = 0; count < cookies.length; count++) {
      let [key, value] = cookies[count].split('=');
      if (key.trim() === cookieName) return value;
    }
    return null;
  }

  static updateUrlParams = (
    url: string,
    params: Record<string, string | number>,
  ) => {
    try {
      const urlObject = new URL(url);
      const queryParams = new URLSearchParams(urlObject.search);
      Object.entries(params).map(([param, value]) => {
        queryParams.set(param, value.toString());
      });
      urlObject.search = queryParams.toString();
      return urlObject.toString();
    } catch (error) {
      console.log(error);
    }
    return url;
  }

  static convertTo3pLite = (url: string) => {
    try {
      return url.replace(
        /\/newz(?=\/(?!shp|lite|cards|nic|fast-cards|video-lite))/g,
        '/newz/shp',
      );
    } catch (error) {
      console.error('Error updating newz URL:', error);
    }
    return url;
  };
}

export default Utils;



// WEBPACK FOOTER //
// ./src/Utils.tsx